import { useState, useEffect } from 'react';

/**
 * Debounce a value — returns the value only after `delay` ms without changes.
 * Useful for search inputs to avoid firing API calls on every keystroke.
 *
 * @example
 * const [search, setSearch] = useState('');
 * const debouncedSearch = useDebounce(search, 300);
 * useEffect(() => { if (debouncedSearch) api.search(debouncedSearch); }, [debouncedSearch]);
 */
export function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debounced;
}

/**
 * Debounce a callback — returns a stable function that only executes after `delay` ms.
 * Useful for inline handlers that should not fire on every interaction.
 *
 * @example
 * const onSearch = useDebouncedCallback((q) => api.search(q), 300);
 * <input onChange={e => onSearch(e.target.value)} />
 */
export function useDebouncedCallback(callback, delay = 300) {
  const [timer, setTimer] = useState(null);

  useEffect(() => () => { if (timer) clearTimeout(timer); }, [timer]);

  return (...args) => {
    if (timer) clearTimeout(timer);
    const newTimer = setTimeout(() => callback(...args), delay);
    setTimer(newTimer);
  };
}
